import React, { useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/lib/auth";

/**
 * Row-level delete button (Super Admin only).
 * Renders nothing for other roles so the UI stays clean for operators.
 *
 * Props:
 *  - endpoint: string, e.g. "/receipts/abc123" (relative to API base)
 *  - label: human label for the confirm dialog, e.g. "receipt TXN-1234"
 *  - onDeleted: callback after successful delete
 *  - supportsForce: if true, show a "Force cascade" checkbox in the dialog
 *  - testId: base for data-testid; produces `<testId>-open` and `<testId>-confirm`
 *  - size: button size (default "icon")
 *  - variant: button variant (default "ghost")
 */
export function RowDeleteButton({
  endpoint, label, onDeleted, supportsForce = true,
  testId = "row-delete", size = "icon", variant = "ghost",
}) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [force, setForce] = useState(false);
  const [busy, setBusy] = useState(false);
  if (user?.role !== "superadmin") return null;

  const run = async () => {
    setBusy(true);
    try {
      const url = supportsForce && force ? `${endpoint}?force=true` : endpoint;
      const { data } = await api.delete(url);
      if (data?.cascade) {
        const parts = Object.entries(data.cascade).filter(([, v]) => v > 0)
          .map(([k, v]) => `${v} ${k}`).join(", ");
        toast.success(parts ? `Deleted (cascade: ${parts})` : "Deleted");
      } else {
        toast.success("Deleted");
      }
      setOpen(false);
      setForce(false);
      onDeleted?.();
    } catch (err) {
      const detail = formatApiError(err.response?.data?.detail);
      // Auto-suggest force if server hints at it and the user hasn't enabled it yet.
      if (!force && supportsForce && err.response?.status === 409 && /force=true/i.test(detail || "")) {
        toast.error(`${detail} — tick "Force cascade" to override.`);
      } else {
        toast.error(detail || "Delete failed");
      }
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        aria-label={`Delete ${label}`}
        data-testid={`${testId}-open`}
      >
        <Trash2 className="w-4 h-4" />
      </Button>
      <AlertDialog open={open} onOpenChange={(v) => { if (!busy) { setOpen(v); if (!v) setForce(false); } }}>
        <AlertDialogContent onClick={(e) => e.stopPropagation()} data-testid={`${testId}-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {label}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the record and writes an audit-log entry.
              {supportsForce && " If this record has linked children, tick Force cascade below to remove everything downstream."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {supportsForce && (
            <label className="flex items-start gap-2 rounded border border-rose-200 bg-rose-50 p-3 text-sm">
              <Checkbox checked={force} onCheckedChange={setForce} data-testid={`${testId}-force`} />
              <span>
                <span className="font-semibold text-rose-800">Force cascade</span>
                <span className="block text-xs text-rose-700 mt-0.5">
                  Removes every linked child record (allocations, receipts, invoices, pro-formas
                  belonging to this row). Use for cleaning up dirty data.
                </span>
              </span>
            </label>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy} data-testid={`${testId}-cancel`}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy}
              onClick={(e) => { e.preventDefault(); run(); }}
              className="bg-rose-600 hover:bg-rose-700 focus:ring-rose-600"
              data-testid={`${testId}-confirm`}
            >
              {busy ? "Deleting…" : force ? "Force delete" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
